import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector, useAuth } from '../hooks';

import { MandateActions } from '../actions/mandate/mandate.actions';
import { EmployeeActions } from '../actions/employee/employee.actions';

import type { Mandate } from '../interfaces/client/mandate.interface';
import type { Employee } from '../interfaces/employee/employee.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import InlineStagePicker from './components/InlineStagePicker';
import AvatarPile from './components/AvatarPile';
import InlineProgressEditor from './components/InlineProgressEditor';
import FilterChipStrip from './components/FilterChipStrip';

import CreateMandateModal from './CreateMandateModal';
import {
  formatZar,
  deriveStage,
  deriveProgress,
  deriveLead,
  deriveCode,
  deriveId,
  initialsFromName,
  hash,
  formatDate,
  num
} from './lib/mandate-helpers';
import { exportCsv, type CsvColumn } from './lib/csv-export';
import { useTableSort, SortableTh } from './lib/use-table-sort';
import FilterMenu, { applyFilters } from './components/FilterMenu';

// =============================================================================
//  Mandates list — v3 redesign
//
//  Data: real mandates from /mandates endpoint (already loaded by the global
//        mandate_store).  Stage/progress/lead/team are stubbed deterministically
//        per-mandate id where the back-end doesn't expose them yet (see
//        ./lib/mandate-helpers.ts).
// =============================================================================

type StageKey =
  | 'All'
  | 'Execution'
  | 'Due Diligence'
  | 'Pre-listing'
  | 'Implementation'
  | 'Closing'
  | 'Reporting'
  | 'Structuring'
  | 'Field Work'
  | 'Closed';

const STAGE_FILTERS: ReadonlyArray<{ value: StageKey; label: string }> = [
  { value: 'All', label: 'All' },
  { value: 'Execution', label: 'Execution' },
  { value: 'Due Diligence', label: 'Due Diligence' },
  { value: 'Pre-listing', label: 'Pre-listing' },
  { value: 'Implementation', label: 'Implementation' },
  { value: 'Closing', label: 'Closing' },
  { value: 'Reporting', label: 'Reporting' },
  { value: 'Structuring', label: 'Structuring' },
  { value: 'Field Work', label: 'Field Work' },
  { value: 'Closed', label: 'Closed' }
];

const MandatesListV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const mandates: Mandate[] = useAppSelector(({ mandate_store }) => mandate_store.mandates) || [];
  const employees: Employee[] =
    useAppSelector(({ employee_store }) => employee_store.employees) || [];

  const [stageFilter, setStageFilter] = useState<StageKey>('All');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [extraFilters, setExtraFilters] = useState<Record<string, string>>({});

  useEffect(() => {
    dispatch(MandateActions.loadMandates(noOp));
    dispatch(EmployeeActions.loadEmployees(noOp));
  }, [dispatch]);

  // Deleting is a hard delete that also purges the mandate's documents, so it
  // asks for confirmation on the row rather than firing on a single click.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | number | null>(null);
  const [deletingId, setDeletingId] = useState<string | number | null>(null);

  const handleDelete = (m: Mandate) => {
    if (!m.id || deletingId) return;
    setDeletingId(m.id);
    dispatch(
      MandateActions.deleteMandate(m.id, () => {
        setDeletingId(null);
        setConfirmDeleteId(null);
      })
    );
  };

  const activeCount = useMemo(
    () => mandates.filter((m) => deriveStage(m) !== 'Closed').length,
    [mandates]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return mandates.filter((m) => {
      const stage = deriveStage(m);
      if (stageFilter !== 'All' && stage !== stageFilter) return false;
      if (q.length > 0) {
        const hay =
          `${m.name || ''} ${m.client || ''} ${m.transaction_type || ''} ${m.sector || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [mandates, stageFilter, search]);

  // Fields offered by the Filter menu.  Options are derived from the rows on
  // screen, so nothing offered can return an empty list.
  const FILTER_FIELDS = useMemo(
    () => [
      { key: 'type', label: 'Transaction type', accessor: (m: Mandate) => m.transaction_type },
      { key: 'sector', label: 'Sector', accessor: (m: Mandate) => m.sector },
      { key: 'client', label: 'Client', accessor: (m: Mandate) => m.client }
    ],
    []
  );
  const filteredPlus = useMemo(
    () => applyFilters(filtered, FILTER_FIELDS, extraFilters),
    [filtered, FILTER_FIELDS, extraFilters]
  );

  // What each sortable column compares on.  Kept next to the table it drives.
  const SORT_ACCESSORS = useMemo(
    () => ({
      project: (m: Mandate) => deriveCode(m),
      client: (m: Mandate) => m.client,
      type: (m: Mandate) => m.transaction_type,
      sector: (m: Mandate) => m.sector,
      stage: (m: Mandate) => deriveStage(m),
      value: (m: Mandate) => num(m.transaction_size),
      progress: (m: Mandate) => deriveProgress(m),
      lead: (m: Mandate) => deriveLead(m),
      date: (m: Mandate) => (m.expected_end_date ? new Date(m.expected_end_date as string) : null)
    }),
    []
  );
  const { sorted, sort, toggle } = useTableSort(filteredPlus, SORT_ACCESSORS);

  // Client-side CSV — no /mandates.csv backend endpoint exists yet.
  // Honours current stage chip + search filters so the exported file
  // matches the visible table.
  const handleExport = () => {
    const columns: ReadonlyArray<CsvColumn<(typeof mandates)[0]>> = [
      { header: 'Code', accessor: (m) => deriveId(m) },
      { header: 'Project', accessor: (m) => deriveCode(m) },
      { header: 'Client', accessor: (m) => m.client },
      { header: 'Type', accessor: (m) => m.transaction_type },
      { header: 'Sector', accessor: (m) => m.sector },
      { header: 'Stage', accessor: (m) => deriveStage(m) },
      { header: 'Progress %', accessor: (m) => deriveProgress(m) },
      { header: 'Transaction size (ZAR)', accessor: (m) => num(m.transaction_size) || '' },
      { header: 'Lead', accessor: (m) => deriveLead(m) },
      { header: 'Signed', accessor: (m) => m.signed },
      { header: 'Start', accessor: (m) => m.start_date },
      { header: 'Target close', accessor: (m) => m.expected_end_date }
    ];
    exportCsv(filtered, columns, 'Mandates');
  };

  return (
    <>
      <Topbar
        crumbs={['Deal Flow', 'Mandates']}
        tools={
          <button
            className="btn ghost"
            type="button"
            onClick={handleExport}
            disabled={mandates.length === 0}
            title={
              filtered.length === mandates.length
                ? `Download CSV of all ${mandates.length} mandates`
                : `Download CSV of ${filtered.length} filtered mandates`
            }
          >
            <Icon name="download" size={14} />
            Export
          </button>
        }
      />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Deal Flow</div>
            <h1 className="page-title">Mandates</h1>
            <div className="page-sub">
              {filtered.length === mandates.length
                ? `${mandates.length} total · ${activeCount} active`
                : `${filtered.length} of ${mandates.length} · ${activeCount} active`}
            </div>
          </div>
          <div className="page-tools">
            <label className="search">
              <Icon name="search" size={14} />
              <input
                type="text"
                placeholder="Search mandates…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <FilterMenu<Mandate>
              rows={filtered}
              fields={FILTER_FIELDS}
              value={extraFilters}
              onChange={setExtraFilters}
            />
            <button className="btn primary" type="button" onClick={() => setCreating(true)}>
              <Icon name="plus" size={14} />
              New mandate
            </button>
          </div>
        </div>

        <FilterChipStrip<StageKey>
          options={STAGE_FILTERS}
          value={stageFilter}
          onChange={setStageFilter}
        />

        <div className="card flush">
          {filtered.length === 0 ? (
            <div className="muted" style={{ padding: 48, textAlign: 'center', fontSize: 13.5 }}>
              No mandates match these filters.
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th className="pin-cell">{/* pin */}</th>
                  <SortableTh label="Project" sortKey="project" sort={sort} onToggle={toggle} />
                  <SortableTh label="Client" sortKey="client" sort={sort} onToggle={toggle} />
                  <SortableTh label="Type" sortKey="type" sort={sort} onToggle={toggle} />
                  <SortableTh label="Sector" sortKey="sector" sort={sort} onToggle={toggle} />
                  <SortableTh label="Stage" sortKey="stage" sort={sort} onToggle={toggle} />
                  <SortableTh
                    label="Value"
                    sortKey="value"
                    sort={sort}
                    onToggle={toggle}
                    className="right"
                  />
                  <SortableTh label="Progress" sortKey="progress" sort={sort} onToggle={toggle} />
                  <SortableTh label="Lead" sortKey="lead" sort={sort} onToggle={toggle} />
                  <th>Team</th>
                  <SortableTh label="Target close" sortKey="date" sort={sort} onToggle={toggle} />
                  {isAdmin && <th className="actions-cell" />}
                </tr>
              </thead>
              <tbody>
                {sorted.map((m) => {
                  // The backend has no mandate↔personnel join, so there is
                  // nothing real to show here.  This used to render a
                  // hash-picked pile of colleagues' initials, putting real
                  // people on deals they had no part in.
                  const team: string[] = [];
                  // Likewise the lead: only the real `lead` column is shown.
                  // The old fallback picked an arbitrary employee by id hash
                  // and displayed them as the named deal lead.
                  const lead = deriveLead(m) || '—';
                  return (
                    <tr
                      key={m.id}
                      onClick={() => navigate(`/mandates/${m.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="pin-cell" aria-hidden="true">
                        <Icon name="pin" size={14} />
                      </td>
                      <td className="strong">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span>{deriveCode(m)}</span>
                          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                            {deriveId(m)}
                          </span>
                        </div>
                      </td>
                      <td>{m.client || '—'}</td>
                      <td>{m.transaction_type || '—'}</td>
                      <td>{m.sector ? <span className="chip muted">{m.sector}</span> : '—'}</td>
                      <td>
                        <InlineStagePicker mandate={m} />
                      </td>
                      <td className="num right strong">{formatZar(m.transaction_size)}</td>
                      <td>
                        <InlineProgressEditor mandate={m} />
                      </td>
                      <td>{lead}</td>
                      <td>
                        <AvatarPile team={team} />
                      </td>
                      <td className="num">{formatDate(m.expected_end_date)}</td>
                      {isAdmin && (
                        <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                          {confirmDeleteId === m.id ? (
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                              <button
                                className="btn ghost"
                                type="button"
                                disabled={deletingId === m.id}
                                onClick={() => setConfirmDeleteId(null)}
                              >
                                Cancel
                              </button>
                              <button
                                className="btn danger"
                                type="button"
                                disabled={deletingId === m.id}
                                onClick={() => handleDelete(m)}
                                title="Permanently deletes this mandate and any documents attached to it"
                              >
                                {deletingId === m.id ? 'Deleting…' : 'Confirm delete'}
                              </button>
                            </div>
                          ) : (
                            <button
                              className="btn ghost"
                              type="button"
                              onClick={() => m.id && setConfirmDeleteId(m.id)}
                              title="Delete this mandate"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {creating && <CreateMandateModal onClose={() => setCreating(false)} />}
    </>
  );
};

export default MandatesListV3;
